import { useEffect, useRef, useState } from 'react';

interface TypewriterTextProps {
  text: string;
  speed?: number;
  startDelay?: number;
  startWhen?: boolean;
  className?: string;
  showCursor?: boolean;
  onComplete?: () => void;
}

export default function TypewriterText({
  text,
  speed = 35,
  startDelay = 0,
  startWhen = true,
  className = '',
  showCursor = true,
  onComplete,
}: TypewriterTextProps) {
  const [displayed, setDisplayed] = useState('');
  const [inView, setInView] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const inViewRef = useRef(false);
  onCompleteRef.current = onComplete;

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Stable observer — only created once, uses ref for guard
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inViewRef.current) {
          inViewRef.current = true;
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || !startWhen || done) return;

    timeoutRef.current = setTimeout(() => {
      let i = 0;
      intervalRef.current = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setDone(true);
          onCompleteRef.current?.();
        }
      }, speed);
    }, startDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [inView, startWhen, done, text, speed, startDelay]);

  const isAnimating = inView && startWhen && !done;

  return (
    <span ref={ref} className={className}>
      {/* Full text always in DOM for SEO and screen readers, visually hidden during animation */}
      <span className={isAnimating ? 'sr-only' : ''}>
        {text}
      </span>
      {/* Animated typing overlay: only rendered during animation */}
      {(inView && startWhen && !done) && (
        <span aria-hidden="true">
          {displayed}
          {showCursor && (
            <span className="inline-block w-[2px] h-[1em] bg-primary-500 ml-0.5 align-middle animate-pulse" />
          )}
        </span>
      )}
    </span>
  );
}